"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { BrandLogoStacked } from "@/components/brand-logo";
import { useTheme } from "@/components/theme-provider";
import { RichText } from "@/components/rich-text";

type Props = {
  title: string;
  lead: string;
};

export function HomeHero({ title, lead }: Props) {
  const t = useTranslations("home");
  const { resolved } = useTheme();
  const dark = resolved === "dark";

  return (
    <section className="relative isolate overflow-hidden">
      <div
        className={
          dark
            ? "absolute inset-0 -z-10 bg-[linear-gradient(160deg,#0a0a0a_0%,#1a1510_40%,#3b2c1e_72%,#2f4a28_100%)]"
            : "absolute inset-0 -z-10 bg-[linear-gradient(160deg,#f6f3ee_0%,#ebe4d8_45%,#d9cfc0_100%)]"
        }
        aria-hidden
      />
      <div
        className={
          dark
            ? "absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_70%_20%,rgba(255,255,255,0.1),transparent_50%)]"
            : "absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_70%_20%,rgba(47,74,40,0.08),transparent_55%)]"
        }
        aria-hidden
      />

      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-4 py-10 md:flex-row md:items-center md:gap-16 md:py-14">
        <div className="flex shrink-0 justify-center md:w-[42%]">
          <BrandLogoStacked
            alt={title}
            className="h-auto w-full max-w-[17rem] sm:max-w-[20rem] md:max-w-none"
            priority
          />
        </div>

        <div
          className={`max-w-xl space-y-6 ${dark ? "text-white" : "text-stone-900"}`}
        >
          <RichText
            html={lead}
            className={`text-lg leading-relaxed md:text-xl [&_h1]:text-[1.65rem] [&_h1]:leading-snug md:[&_h1]:text-3xl [&_h2]:text-lg [&_h2]:font-medium md:[&_h2]:text-xl [&_p]:mb-2 ${dark ? "text-stone-200 [&_h1]:text-white [&_h2]:text-stone-100 [&_h3]:text-white" : "text-stone-700"}`}
          />
          <div className="flex flex-wrap gap-3">
            <Link
              href="/contact"
              className={
                dark
                  ? "rounded-md bg-white px-4 py-2.5 text-sm font-medium text-stone-950 hover:bg-stone-100"
                  : "rounded-md bg-stone-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-stone-800"
              }
            >
              {t("ctaContact")}
            </Link>
            <Link
              href="/units"
              className={
                dark
                  ? "rounded-md border border-white/40 px-4 py-2.5 text-sm font-medium text-white hover:bg-white/10"
                  : "rounded-md border border-stone-400 px-4 py-2.5 text-sm font-medium text-stone-900 hover:bg-black/5"
              }
            >
              {t("ctaUnits")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
